import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons'
import { Card } from 'antd'
import AuthLogo from '@/features/auth/components/AuthLogo'

export type PlatformConnectionStatus = 'connecting' | 'success' | 'error'

type Props = {
  platformName: 'MYOB' | 'QuickBooks'
  status: PlatformConnectionStatus
  errorMessage?: string
}

const statusContent = {
  connecting: {
    title: (platform: string) => `Connecting to ${platform}`,
    description: 'Please wait while we securely complete your connection.',
  },
  success: {
    title: (platform: string) => `Successfully connected to ${platform}`,
    description: 'You can return to Dashboard Insights. This window will close automatically.',
  },
  error: {
    title: (platform: string) => `Unable to connect to ${platform}`,
    description: 'Return to Dashboard Insights and try connecting again.',
  },
} satisfies Record<PlatformConnectionStatus, { title: (platform: string) => string; description: string }>

export default function PlatformConnectionScreen({ platformName, status, errorMessage }: Props) {
  const content = statusContent[status]
  const role = status === 'error' ? 'alert' : 'status'

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#d1e8e8] px-4 py-8 sm:px-6">
      <div className="flex w-full max-w-lg flex-col items-center">
        <AuthLogo className="mb-6 h-12 w-auto sm:h-14" />
        <Card className="w-full rounded-2xl border border-[#bde2e1] text-center shadow-sm">
          <div
            role={role}
            aria-live={status === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
            data-cy={`platform-connection-${status}`}
            className="flex flex-col items-center px-2 py-5 sm:px-8 sm:py-8"
          >
            {status === 'connecting' && (
              <LoadingOutlined className="mb-5 text-5xl text-[#15B79E]" spin aria-hidden="true" />
            )}
            {status === 'success' && (
              <CheckCircleFilled className="mb-5 text-5xl text-[#15B79E]" aria-hidden="true" />
            )}
            {status === 'error' && (
              <CloseCircleFilled className="mb-5 text-5xl text-red-500" aria-hidden="true" />
            )}
            <h1 className="mb-2 text-xl font-semibold text-slate-900 sm:text-2xl">
              {content.title(platformName)}
            </h1>
            <p className="m-0 text-sm leading-6 text-slate-600 sm:text-base">
              {status === 'error' && errorMessage ? errorMessage : content.description}
            </p>
          </div>
        </Card>
      </div>
    </main>
  )
}
